import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { Flex } from "@redotech/redo-web/flex";
import { memo, type ReactNode } from "react";
import * as css from "./modalless-page-layout.module.css";

export interface ModallessPageLayoutProps {
  children: ReactNode;
  /**
   * Content column width on desktop. `narrow` is the 624px reading column used
   * by the linear submission steps; `2xl`/`3xl` widen past the default.
   */
  contentWidth?: "narrow" | "2xl" | "3xl";
  footer?: ReactNode;
  footerUiSelector?: string;
  fullWidth?: boolean;
  stickyFooter?: boolean;
  topBar?: ReactNode;
  uiSelector: string;
}

export const ModallessPageLayout = memo(function ModallessPageLayout({
  children,
  contentWidth,
  footer,
  footerUiSelector,
  fullWidth,
  stickyFooter = true,
  topBar,
  uiSelector,
}: ModallessPageLayoutProps) {
  const screen = useScreenType();
  const isDesktop = screen === ScreenType.DESKTOP;
  const isFullWidth = !!fullWidth && isDesktop;
  // 624px is off the `maxW` scale, so the narrow column comes from a class.
  // Mobile stays full-bleed either way.
  const isNarrowColumn = contentWidth === "narrow" && isDesktop && !isFullWidth;
  const contentMaxWidth = isFullWidth
    ? "6xl"
    : contentWidth === "narrow"
      ? undefined
      : (contentWidth ?? "2xl");
  const footerElement = footer ? (
    <Flex
      bgColor="primary"
      borderColor="primary"
      borderStyle="solid"
      borderTopWidth="1px"
      data-modalless-footer=""
      data-ui={footerUiSelector ?? `${uiSelector}-footer`}
      dir="column"
      pb={isDesktop ? "6xl" : "xl"}
      px={isDesktop ? "6xl" : "xl"}
      shrink={0}
      w="full"
    >
      {footer}
    </Flex>
  ) : null;

  return (
    <Flex
      className={css.root}
      data-modalless-page=""
      data-ui={uiSelector}
      flexDirection="column"
      overflow="hidden"
      w="full"
    >
      {topBar}

      <Flex
        align="center"
        flexDirection="column"
        grow={1}
        justify={stickyFooter ? undefined : "space-between"}
        minH="none"
        overflowY="auto"
        px={isDesktop ? "xl" : "none"}
        shrink={1}
        w="full"
      >
        <Flex
          className={isNarrowColumn ? css.narrowContent : undefined}
          flexDirection="column"
          gap="3xl"
          maxW={contentMaxWidth}
          px={isDesktop ? (fullWidth || isNarrowColumn ? "none" : "5xl") : "xl"}
          py={isDesktop ? "8xl" : "3xl"}
          w="full"
        >
          {children}
        </Flex>
        {!stickyFooter && footerElement}
      </Flex>

      {stickyFooter && footerElement}
    </Flex>
  );
});
